<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  company: { type: Object, default: null },
});

const emit = defineEmits(['close', 'submit']);

const { t } = useI18n();

const name = ref('');
const domain = ref('');
const description = ref('');
const isSubmitting = ref(false);

const isEditing = computed(() => !!props.company);

const modalTitle = computed(() =>
  isEditing.value
    ? t('COMPANIES.FORM.EDIT_TITLE')
    : t('COMPANIES.FORM.CREATE_TITLE')
);

const submitLabel = computed(() =>
  isEditing.value ? t('COMPANIES.FORM.UPDATE') : t('COMPANIES.FORM.CREATE')
);

const isFormValid = computed(() => name.value.trim().length > 0);

watch(
  () => props.show,
  newVal => {
    if (newVal && props.company) {
      name.value = props.company.name || '';
      domain.value = props.company.domain || '';
      description.value = props.company.description || '';
    } else if (newVal) {
      name.value = '';
      domain.value = '';
      description.value = '';
    }
  }
);

const onClose = () => {
  emit('close');
};

const onSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    const data = {
      name: name.value.trim(),
      domain: domain.value.trim(),
      description: description.value.trim(),
    };
    if (isEditing.value) {
      data.id = props.company.id;
    }
    emit('submit', data);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Modal :show="show" :on-close="onClose">
    <div class="h-auto overflow-auto flex flex-col">
      <woot-modal-header
        :header-title="modalTitle"
        :header-content="
          isEditing
            ? t('COMPANIES.FORM.EDIT_DESC')
            : t('COMPANIES.FORM.CREATE_DESC')
        "
      />
      <form
        class="flex flex-col gap-4 py-4 px-6 w-full"
        @submit.prevent="onSubmit"
      >
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('COMPANIES.FORM.NAME.LABEL') }}
          </label>
          <Input
            v-model="name"
            type="text"
            :placeholder="t('COMPANIES.FORM.NAME.PLACEHOLDER')"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('COMPANIES.FORM.DOMAIN.LABEL') }}
          </label>
          <Input
            v-model="domain"
            type="text"
            :placeholder="t('COMPANIES.FORM.DOMAIN.PLACEHOLDER')"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('COMPANIES.FORM.DESCRIPTION.LABEL') }}
          </label>
          <textarea
            v-model="description"
            :placeholder="t('COMPANIES.FORM.DESCRIPTION.PLACEHOLDER')"
            rows="3"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:border-n-brand focus:outline-none resize-none"
          />
        </div>
        <div class="flex flex-row justify-end gap-2 pt-2">
          <NextButton
            faded
            type="reset"
            :label="t('COMPANIES.FORM.CANCEL')"
            @click="onClose"
          />
          <NextButton
            type="submit"
            :label="submitLabel"
            :disabled="!isFormValid || isSubmitting"
            @click="onSubmit"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>
